<template>
  <div class="reservation-container">
    <div class="reservation-container-header">
      <div class="header-left">
        <button class="back-btn" @click="$router.back()" title="돌아가기">
          ←
        </button>
        <h1>예매 관리</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="qr-btn" @click="openQrModal" :disabled="loading || reservations.length === 0">
          QR 확인
        </button>
        <button type="button" class="excel-btn" @click="downloadExcel"
          :disabled="loading || filteredReservations.length === 0">
          CSV 다운로드
        </button>
      </div>
    </div>

    <!-- 안내 배너 -->
    <div class="info-banner">
      <span class="info-banner-icon">ℹ️</span>
      <span><strong>확정</strong> 버튼을 누르면 예매자에게 티켓번호가 발급되며, 카카오알림톡이 발송됩니다.</span>
    </div>

    <!-- QR 확인 모달 -->
    <div v-if="showQrModal" class="qr-modal-overlay" @click.self="closeQrModal">
      <div class="qr-modal">
        <div class="qr-modal-header">
          <div class="qr-modal-title">QR 확인</div>
          <button type="button" class="qr-modal-close" @click="closeQrModal" title="닫기">✕</button>
        </div>

        <div class="qr-modal-body">
          <div class="qr-mode-row">
            <button type="button" class="qr-mode-btn" :class="{ active: qrMode === 'camera' }"
              @click="setQrMode('camera')" :disabled="!barcodeSupported">
              카메라 스캔
            </button>
            <button type="button" class="qr-mode-btn" :class="{ active: qrMode === 'manual' }"
              @click="setQrMode('manual')">
              티켓번호 입력
            </button>
          </div>

          <div v-if="qrMode === 'camera'" class="qr-camera-box">
            <div v-if="!barcodeSupported" class="qr-hint">
              이 브라우저는 QR 스캔을 지원하지 않습니다. ‘티켓번호 입력’을 사용해주세요.
            </div>
            <div v-else>
              <video ref="qrVideo" class="qr-video" autoplay playsinline muted></video>
              <div class="qr-camera-actions">
                <button type="button" class="qr-action-btn" @click="startQrScan" :disabled="qrScanning">
                  {{ qrScanning ? "스캔 중..." : "스캔 시작" }}
                </button>
                <button type="button" class="qr-action-btn subtle" @click="stopQrScan" :disabled="!qrScanning">
                  중지
                </button>
              </div>
              <div class="qr-hint">QR을 카메라에 비춰주세요.</div>
            </div>
          </div>

          <div v-else class="qr-manual-box">
            <input v-model="qrManualInput" type="text" class="qr-input" placeholder="티켓번호 입력" />
            <button type="button" class="qr-action-btn" @click="confirmManualQr" :disabled="!qrManualInput.trim()">
              확인
            </button>
            <div class="qr-hint">예: #WA2OMAQM</div>
          </div>

          <div v-if="qrError" class="qr-error">{{ qrError }}</div>
        </div>
      </div>
    </div>

    <div v-if="loading" class="loading">로딩 중...</div>
    <div v-else-if="error" class="error">{{ error }}</div>
    <div v-else>
      <!-- 필터 및 예매 신청 목록 -->
      <section class="list-section">
        <div class="list-header">
          <h2>
            예매 신청 내역
            <span v-if="totalTicketCount > 0" class="ticket-count-info">({{ totalReservedTickets }} / {{
              totalTicketCount }})</span>
            <span v-if="totalTicketCount > 0" class="ticket-count-checkedin">현재 {{ checkedInPeopleCount }}명 입장 완료</span>
          </h2>

          <!-- 상태 필터 -->
          <div class="status-filter">
            <button type="button" class="filter-btn" :class="{ selected: filterStatus === 'all' }"
              @click="filterStatus = 'all'">
              전체 ({{ reservationsTotalQuantity }})
            </button>
            <button type="button" class="filter-btn" :class="{ selected: filterStatus === 'pending' }"
              @click="filterStatus = 'pending'">
              대기중 ({{ pendingCount }})
            </button>
            <button type="button" class="filter-btn" :class="{ selected: filterStatus === 'confirmed' }"
              @click="filterStatus = 'confirmed'">
              확정 ({{ confirmedCount }})
            </button>
            <button type="button" class="filter-btn" :class="{ selected: filterStatus === 'cancelled' }"
              @click="filterStatus = 'cancelled'">
              취소 ({{ cancelledCount }})
            </button>
            <button v-if="refundCount > 0" type="button" class="filter-btn"
              :class="{ selected: filterStatus === 'refund' }" @click="filterStatus = 'refund'">
              환불 ({{ refundCount }})
            </button>
          </div>
        </div>

        <!-- 검색 및 정렬 -->
        <div class="controls">
          <div class="search-sort-row">
            <div class="search-box">
              <input v-model="searchKeyword" type="text" class="search-input"
                :placeholder="reservationSearchPlaceholder" />
            </div>
            <div class="sort-box">
              <button @click="toggleSort" class="sort-button">
                {{ getSortButtonText() }}
                <img class="sort-icon" src="@/assets/images/swap_vert.png" alt="정렬 변경" />
              </button>
            </div>
          </div>
        </div>

        <div class="reservations-list">
          <div v-if="filteredReservations.length === 0" class="no-data">
            {{ searchKeyword.trim() ? "검색 결과가 없습니다." : "예매 신청이 없습니다." }}
          </div>
          <div v-else v-for="reservation in filteredReservations" :key="reservation.id" class="reservation-card"
            :class="{ 'qr-highlight': highlightReservationId === reservation.id }"
            :ref="(el) => setReservationRef(reservation.id, el)">
            <!-- 리스트 뷰 (기본) -->
            <div class="reservation-summary" @click="toggleReservationDetail(reservation.id)">
              <div class="summary-main">
                <span class="summary-name">{{ reservation.name }}</span>
                <span class="summary-email">{{ formatReservationEmailForDisplay(reservation.email) }}</span>
                <span class="summary-quantity">{{ reservation.quantity }}매</span>
              </div>
              <div class="summary-status">
                <span v-if="!reservation.isInviteTicket" class="summary-amount">{{ formatAmount(reservation.totalAmount)
                }}</span>
                <span v-if="reservation.isInviteTicket" class="invite-ticket-label">🎟️ 초대권</span>
                <span v-else class="reservation-status" :class="getUnifiedStatusKey(reservation)">
                  {{ getUnifiedStatusText(reservation) }}
                </span>
                <span v-if="canCheckIn(reservation) && reservation.checkedIn" class="checked-in-badge">✓ 입장완료</span>
                <span v-else-if="canCheckIn(reservation)" class="not-checked-in-badge">미입장</span>
                <span class="expand-icon">
                  {{ isExpanded(reservation.id) ? "▲" : "▼" }}
                </span>
              </div>
            </div>

            <!-- 상세 뷰 (펼쳐졌을 때만) -->
            <div v-if="isExpanded(reservation.id)" class="reservation-detail">
              <div class="detail-section">
                <div class="detail-row">
                  <span class="detail-label">예매자:</span>
                  <span class="detail-value">{{ reservation.name }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">티켓번호:</span>
                  <span class="detail-value">#{{ getTicketNo(reservation.id) }}</span>
                </div>

                <div class="detail-row">
                  <span class="detail-label">이메일:</span>
                  <div class="detail-value-with-action">
                    <span>{{ formatReservationEmailForDisplay(reservation.email) }}</span>
                    <button v-if="!isLivePerformanceEnded" @click.stop="copyEmail(reservation.email)"
                      class="copy-btn-icon" title="이메일 복사">
                      <img src="@/assets/images/file_copy_200.svg" alt="복사" />
                    </button>
                  </div>
                </div>
                <div v-if="reservation.phone" class="detail-row">
                  <span class="detail-label">연락처:</span>
                  <div class="detail-value-with-action">
                    <span>{{ formatPhone(reservation.phone) }}</span>
                    <button @click.stop="copyText(reservation.phone)" class="copy-btn-icon" title="전화번호 복사">
                      <img src="@/assets/images/file_copy_200.svg" alt="복사" />
                    </button>
                  </div>
                </div>
                <div class="detail-row">
                  <span class="detail-label">신청일시:</span>
                  <span class="detail-value">{{
                    formatDate(reservation.createdAt)
                  }}</span>
                </div>
                <div v-if="!reservation.isInviteTicket && reservation.totalAmount" class="detail-row">
                  <span class="detail-label">예매 금액:</span>
                  <span class="detail-value">{{ formatAmount(reservation.totalAmount) }}</span>
                </div>
                <!-- 커스텀 필드 답변 -->
                <div v-if="hasCustomFieldAnswers(reservation)" class="custom-answers-section">
                  <div class="custom-answers-title"><span>추가 정보</span></div>
                  <div v-for="(answer, index) in getCustomFieldAnswers(reservation)" :key="index" class="detail-row">
                    <span class="detail-label">{{ answer.question }}:</span>
                    <span class="detail-value">{{ answer.answer }}</span>
                  </div>
                </div>

                <!-- 티켓 패키지 선택 내역 -->
                <div v-if="!reservation.isInviteTicket && getRewardSummary(reservation).length"
                  class="custom-answers-section">
                  <div class="custom-answers-title"><span>티켓 패키지</span></div>
                  <div v-for="(rw, idx) in getRewardSummary(reservation)" :key="'rw-' + idx" class="detail-row">
                    <span class="detail-label">{{ rw.name }}:</span>
                    <span class="detail-value">{{ rw.quantity }}개 · {{ rw.subtotal }}</span>
                  </div>
                  <div v-if="reservation.rewardTotal" class="detail-row purchase-total-row">
                    <span class="detail-label">패키지 합계:</span>
                    <span class="detail-value purchase-total-value">{{ formatAmount(reservation.rewardTotal) }}</span>
                  </div>
                </div>

                <!-- 추가구매항목 -->
                <div v-if="!reservation.isInviteTicket && getPurchaseSummary(reservation).length"
                  class="custom-answers-section">
                  <div class="custom-answers-title"><span>추가구매항목</span></div>
                  <div v-for="(g, idx) in getPurchaseSummary(reservation)" :key="'g-' + idx" class="detail-row">
                    <span class="detail-label">{{ g.name }}:</span>
                    <span class="detail-value">
                      {{ g.quantity }}개 · {{ g.subtotal }}
                      <span v-if="g.optionAnswer" class="purchase-option-text">({{ g.optionAnswer }})</span>
                    </span>
                  </div>
                  <div v-if="reservation.purchaseTotal" class="detail-row purchase-total-row">
                    <span class="detail-label">추가구매 합계:</span>
                    <span class="detail-value purchase-total-value">{{ formatAmount(reservation.purchaseTotal) }}</span>
                  </div>
                </div>
              </div>

              <div class="reservation-actions">
                <template v-if="reservation.isInviteTicket">
                  <button @click="deleteInviteReservation(reservation)" class="btn-cancel">
                    초대권 삭제
                  </button>
                  <button v-if="canCheckIn(reservation) && !reservation.checkedIn"
                    @click="markCheckedIn(reservation.id)" class="btn-check-in">
                    입장확인
                  </button>
                  <button v-if="reservation.checkedIn" @click="unmarkCheckedIn(reservation.id)" class="btn-uncheck-in">
                    입장취소
                  </button>
                </template>
                <template v-else-if="reservation.status === 'refund_completed'">
                  <div class="refund-completed-badge">환불 완료</div>
                </template>
                <template v-else>
                  <button class="payment-btn" :class="{ confirmed: reservation.paymentConfirmed }" @click="
                    togglePaymentStatus(
                      reservation.id,
                      reservation.paymentConfirmed
                    )
                    ">
                    {{
                      reservation.paymentConfirmed
                        ? "✓ 입금 확인됨"
                        : "입금 확인"
                    }}
                  </button>
                  <button v-if="reservation.status !== 'confirmed'"
                    @click="updateReservationStatus(reservation.id, 'confirmed', reservation)" class="btn-confirm">
                    확정
                  </button>
                  <button v-if="reservation.status !== 'pending'"
                    @click="updateReservationStatus(reservation.id, 'pending')" class="btn-pending">
                    대기중으로 변경
                  </button>
                  <button v-if="reservation.status === 'refund_requested'"
                    @click="updateReservationStatus(reservation.id, 'refund_completed')" class="btn-refund-complete">
                    환불 완료
                  </button>
                  <button v-if="reservation.status !== 'cancelled'"
                    @click="updateReservationStatus(reservation.id, 'cancelled')" class="btn-cancel">
                    취소
                  </button>
                  <button v-if="canCheckIn(reservation) && !reservation.checkedIn"
                    @click="markCheckedIn(reservation.id)" class="btn-check-in">
                    입장확인
                  </button>
                  <button v-if="reservation.checkedIn" @click="unmarkCheckedIn(reservation.id)" class="btn-uncheck-in">
                    입장취소
                  </button>
                </template>
              </div>
            </div>
          </div>

          <!-- 집계 카드 -->
          <div v-if="filteredReservations.length > 0" class="summary-stats-card">
            <div class="summary-stats-content">
              <span class="stats-label">총 예매 금액</span>
              <span class="stats-total">{{ formatAmount(filteredTotalAmount) }}</span>
              <span class="stats-breakdown" v-if="commissionData?.linkookPrice">
                ({{ commissionData.linkookPrice }} x {{ totalTicketsCount }}매)
              </span>
            </div>
            <div v-if="filteredRewardTotalAmount > 0" class="summary-stats-extra">
              <div class="stats-extra-row">
                <span class="stats-extra-label">패키지 합계</span>
                <span class="stats-extra-value">{{ formatAmount(filteredRewardTotalAmount) }}</span>
              </div>
            </div>
            <div v-if="filteredPurchaseTotalAmount > 0" class="summary-stats-extra">
              <div class="stats-extra-row">
                <span class="stats-extra-label">추가구매 합계</span>
                <span class="stats-extra-value">{{ formatAmount(filteredPurchaseTotalAmount) }}</span>
              </div>
              <div v-for="item in filteredPurchaseItemSummary" :key="item.name"
                class="stats-extra-row stats-extra-detail">
                <span class="stats-extra-label">{{ item.name }}</span>
                <span class="stats-extra-value">
                  {{ formatAmount(item.unitPrice) }} x {{ item.totalQuantity }}개 · {{ formatAmount(item.totalAmount) }}
                </span>
              </div>
            </div>
          </div>

          <!-- 초대권 등록 버튼 -->
          <div class="invite-ticket-section">
            <button type="button" class="invite-ticket-btn" @click="openInviteModal">
              🎟️ 초대권 등록
            </button>
          </div>
        </div>
      </section>
    </div>
  </div>

  <!-- 초대권 등록 모달 -->
  <div v-if="showInviteModal" class="invite-modal-overlay" @click.self="closeInviteModal">
    <div class="invite-modal">
      <div class="invite-modal-header">
        <h3 class="invite-modal-title">초대권 등록</h3>
        <button type="button" class="invite-modal-close" @click="closeInviteModal" aria-label="닫기">×</button>
      </div>

      <div class="invite-modal-body">
        <p class="invite-modal-desc">
          링꾹 계정으로 검색하여 특정 사용자에게 초대권을 발급합니다.<br />초대권은 즉시 확정 처리되며, QR 코드가 발급됩니다.
        </p>

        <!-- 등록된 초대권 목록 -->
        <div v-if="invitedMembers.length > 0" class="invite-member-section">
          <h4 class="invite-section-title">발급된 초대권</h4>
          <div class="invite-member-list">
            <div v-for="(member, index) in invitedMembers" :key="index" class="invite-member-item">
              <div class="invite-member-info">
                <img v-if="member.profileImage" :src="member.profileImage" :alt="member.nickname"
                  class="invite-profile-image" />
                <div v-else class="invite-profile-placeholder">
                  {{ inviteAvatarInitial(member) }}
                </div>
                <div class="invite-member-details">
                  <span class="invite-member-nickname">{{ member.nickname || '닉네임 없음' }}</span>
                  <span class="invite-member-email">{{ formatReservationEmailForDisplay(member.email) }}</span>
                </div>
              </div>
              <span class="invite-ticket-badge">초대권</span>
            </div>
          </div>
        </div>

      </div>

      <div class="invite-add-section">
        <h4 class="invite-section-title">사용자 검색</h4>
        <div class="invite-search-form">
          <AutoComplete ref="inviteUserAutocomplete" v-model="inviteSearchQuery" :suggestions="inviteFilteredUsers"
            @complete="searchInviteUsers" @item-select="selectInviteUser"
            :placeholder="isLivePerformanceEnded ? '닉네임으로 검색' : '닉네임 또는 이메일로 검색'" :disabled="inviteLoading"
            optionLabel="label" :forceSelection="false" panelClass="invite-autocomplete-panel"
            class="invite-search-autocomplete" appendTo="self">
            <template #option="{ option }">
              <div class="invite-user-option">
                <img v-if="option.profileImage" :src="option.profileImage" :alt="option.nickname"
                  class="invite-option-profile-image" />
                <div v-else class="invite-option-profile-placeholder">
                  {{ inviteAvatarInitial(option) }}
                </div>
                <div class="invite-option-details">
                  <span class="invite-option-nickname">{{ option.nickname || '닉네임 없음' }}</span>
                  <span class="invite-option-email">{{ formatReservationEmailForDisplay(option.email) }}</span>
                </div>
              </div>
            </template>
          </AutoComplete>
          <button type="button" class="invite-add-btn" @click="registerInviteTicket"
            :disabled="inviteLoading || !inviteSelectedUser">
            {{ inviteLoading ? '등록 중...' : '등록' }}
          </button>
        </div>
      </div>

      <div class="invite-modal-footer">
        <button type="button" class="invite-close-btn" @click="closeInviteModal" :disabled="inviteLoading">닫기</button>
      </div>
    </div>
  </div>
</template>

<script>
import { firestore, storage } from "../../../firebaseConfig";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  query,
  where,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ref as storageRef } from "firebase/storage";
import { getDownloadURL } from "@/utils/storageWithNavigation";
import { updateReservationStatus as updateReservationStatusApi, confirmPayment, createReservation, deleteReservation } from "@/utils/write/purchase";
import { sendAlimtalk } from "@/utils/write/message";
import { createNotification } from "@/utils/write/alarm";
import { useUserStore } from "@/store/user";
import AutoComplete from "primevue/autocomplete";
import jsQR from "jsqr";

export default {
  name: "CommissionReservationList",
  components: {
    AutoComplete,
  },
  data() {
    return {
      userStore: useUserStore(),
      loading: true,
      error: null,
      commissionData: null,
      formInfo: null,
      reservations: [],
      filterStatus: "all",
      searchKeyword: "",
      sortBy: "dateDesc",
      commissionId: this.$route.params.id,
      expandedReservations: new Set(),

      // QR 확인
      showQrModal: false,
      qrMode: "camera",
      qrManualInput: "",
      qrError: "",
      qrScanning: false,
      barcodeSupported: typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia,
      highlightReservationId: null,
      qrStream: null,
      qrDetector: null,

      // 초대권 등록
      showInviteModal: false,
      inviteSearchQuery: "",
      inviteFilteredUsers: [],
      inviteSelectedUser: null,
      inviteLoading: false,
      invitedMembers: [],
    };
  },
  computed: {
    // 공연 종료 여부 — LiveDetail `calculateDaysLeft(date) === '종료'` 와 동일 (공연일 달력 기준). 커미션은 공연일 없음 → 미종료로 간주
    isLivePerformanceEnded() {
      const targetDate = this.commissionData?.date;
      if (!targetDate || typeof targetDate !== "string") return false;
      const substrDate = targetDate.substring(0, 10);
      const m = /^(\d{4})\.(\d{2})\.(\d{2})$/.exec(substrDate);
      if (!m) return false;
      const y = Number(m[1]);
      const mo = Number(m[2]);
      const d = Number(m[3]);
      if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return false;

      const now = new Date();
      const nowMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const eventMidnight = new Date(y, mo - 1, d);
      const daysLeft = Math.ceil(
        (eventMidnight - nowMidnight) / (1000 * 60 * 60 * 24)
      );
      return daysLeft < 0;
    },
    reservationSearchPlaceholder() {
      return this.isLivePerformanceEnded ? "이름 검색" : "이름 또는 이메일 검색";
    },
    // 필터링된 예매 목록 (상태 필터 + 검색 + 정렬)
    filteredReservations() {
      let list;
      if (this.filterStatus === "all") {
        list = [...this.reservations];
      } else if (this.filterStatus === "refund") {
        list = this.reservations.filter((r) => r.status === "refund_requested" || r.status === "refund_completed");
      } else {
        list = this.reservations.filter((r) => r.status === this.filterStatus);
      }

      // 검색
      const kw = (this.searchKeyword || "").trim().toLowerCase();
      if (kw) {
        list = list.filter((r) => {
          const nameMatch = (r.name || "").toLowerCase().includes(kw);
          if (this.isLivePerformanceEnded) return nameMatch;
          return nameMatch || (r.email || "").toLowerCase().includes(kw);
        });
      }

      // 정렬
      const sortBy = this.sortBy || "dateDesc";
      list.sort((a, b) => {
        switch (sortBy) {
          case "dateDesc": {
            const aTime = a.createdAt?.seconds ?? 0;
            const bTime = b.createdAt?.seconds ?? 0;
            return bTime - aTime;
          }
          case "nameAsc":
            return (a.name || "").localeCompare(b.name || "");
          case "quantityDesc":
            return (b.quantity || 0) - (a.quantity || 0);
          default:
            return 0;
        }
      });
      return list;
    },
    // 통계 계산 (필터 탭 숫자는 예약 건수가 아닌 예매 매수)
    reservationsTotalQuantity() {
      return this.reservations.reduce((sum, r) => sum + (r.quantity || 0), 0);
    },
    pendingCount() {
      return this.reservations
        .filter((r) => r.status === "pending")
        .reduce((sum, r) => sum + (r.quantity || 0), 0);
    },
    confirmedCount() {
      return this.reservations
        .filter((r) => r.status === "confirmed")
        .reduce((sum, r) => sum + (r.quantity || 0), 0);
    },
    cancelledCount() {
      return this.reservations
        .filter((r) => r.status === "cancelled")
        .reduce((sum, r) => sum + (r.quantity || 0), 0);
    },
    refundCount() {
      return this.reservations
        .filter((r) => r.status === "refund_requested" || r.status === "refund_completed")
        .reduce((sum, r) => sum + (r.quantity || 0), 0);
    },
    isExcludedFromStats() {
      const excluded = ["cancelled", "refund_requested", "refund_completed"];
      return (r) => excluded.includes(r.status);
    },
    // 총 예매된 티켓 수 (취소/환불 제외)
    totalReservedTickets() {
      return this.reservations
        .filter((r) => !this.isExcludedFromStats(r))
        .reduce((sum, r) => sum + (r.quantity || 0), 0);
    },
    // 총 티켓 수
    totalTicketCount() {
      return this.commissionData?.linkookTicketCount || 0;
    },
    // 입장 완료 인원(매수 합계): 취소·환불 제외, 입장 처리된 예매만
    checkedInPeopleCount() {
      return this.reservations
        .filter((r) => r.checkedIn && !this.isExcludedFromStats(r))
        .reduce((sum, r) => sum + (r.quantity || 0), 0);
    },
    // 총 예매 금액 (취소/환불 제외)
    totalReservationAmount() {
      return this.reservations
        .filter((r) => !this.isExcludedFromStats(r))
        .reduce((sum, r) => sum + (r.totalAmount || 0), 0);
    },
    // 필터링된 예매의 총 인원 수 (취소/환불 제외)
    totalPeopleCount() {
      return this.filteredReservations
        .filter((r) => !this.isExcludedFromStats(r))
        .length;
    },
    // 필터링된 예매의 총 매수 (취소/환불/초대권 제외)
    totalTicketsCount() {
      return this.filteredReservations
        .filter((r) => !this.isExcludedFromStats(r) && !r.isInviteTicket)
        .reduce((sum, r) => sum + (r.quantity || 0), 0);
    },
    // 필터링된 예매의 총 금액 (취소/환불 제외)
    filteredTotalAmount() {
      return this.filteredReservations
        .filter((r) => !this.isExcludedFromStats(r))
        .reduce((sum, r) => sum + (r.totalAmount || 0), 0);
    },
    filteredPurchaseTotalAmount() {
      return this.filteredReservations
        .filter((r) => !this.isExcludedFromStats(r))
        .reduce((sum, r) => sum + (r.purchaseTotal || 0), 0);
    },
    filteredRewardTotalAmount() {
      return this.filteredReservations
        .filter((r) => !this.isExcludedFromStats(r))
        .reduce((sum, r) => sum + (r.rewardTotal || 0), 0);
    },
    filteredPurchaseItemSummary() {
      const items = this.formInfo?.purchaseItems || [];
      if (!items.length) return [];

      return items
        .map((item, index) => {
          const summary = this.filteredReservations
            .filter((r) => !this.isExcludedFromStats(r))
            .reduce((acc, reservation) => {
              const quantity = Number(reservation?.purchaseAnswers?.[index]?.quantity) || 0;
              acc.totalQuantity += quantity;
              return acc;
            }, { totalQuantity: 0 });

          if (!summary.totalQuantity) return null;

          return {
            name: item?.name || `추가구매 ${index + 1}`,
            unitPrice: Number(item?.price) || 0,
            totalQuantity: summary.totalQuantity,
            totalAmount: (Number(item?.price) || 0) * summary.totalQuantity,
          };
        })
        .filter(Boolean);
    },
  },
  async mounted() {
    // 로그인 체크
    if (!this.userStore.isLogin) {
      this.$alert("로그인이 필요합니다.");
      this.$router.push("/");
      return;
    }

    try {
      // 1. 먼저 커미션 정보 로드
      await this.loadCommissionData();

      // 2. 권한 체크 (commissionData 로드 후)
      if (!this.checkAuth()) return;

      // 3. 권한이 있으면 예매 목록 로드
      await this.loadReservations();
    } catch (err) {
      console.error("초기화 오류:", err);
      this.error = "데이터를 불러오는 중 오류가 발생했습니다.";
    } finally {
      this.loading = false;
    }
  },
  beforeUnmount() {
    this.stopQrScan();
  },
  methods: {
    async resolveUserProfileImage(imgValue) {
      const raw = String(imgValue || "").trim();
      if (!raw) return null;
      if (/^https?:\/\//i.test(raw)) return raw;

      const candidates = [];
      if (raw.includes("/")) {
        candidates.push(raw);
      }
      candidates.push(raw.startsWith("profile/") ? raw : `profile/${raw}`);

      for (const path of [...new Set(candidates)]) {
        try {
          const imgRef = storageRef(storage, path);
          return await getDownloadURL(imgRef);
        } catch (_) {
          // 다음 경로 후보 시도
        }
      }

      return null;
    },
    // 예매 상세 펼치기/접기
    toggleReservationDetail(reservationId) {
      const expanded = this.expandedReservations;
      if (expanded.has(reservationId)) {
        expanded.delete(reservationId);
      } else {
        expanded.add(reservationId);
      }
      this.expandedReservations = new Set(expanded);
    },
    setReservationRef(id, el) {
      if (!id) return;
      // Vue가 unmount할 때 null이 올 수 있음
      if (!el) return;
      // NOTE: 여기서 reactive state를 갱신하면 렌더 -> ref 콜백 -> 렌더 루프가 생길 수 있음
      // 따라서 비반응(plain) 저장소를 사용한다.
      if (!this._reservationElMap) this._reservationElMap = {};
      this._reservationElMap[id] = el;
    },
    // 펼쳐져 있는지 확인
    isExpanded(reservationId) {
      return this.expandedReservations.has(reservationId);
    },
    getTicketNo(id) {
      const str = String(id || "");
      if (!str) return "-";
      return str.slice(-8).toUpperCase();
    },
    formatReservationEmailForDisplay(email) {
      if (this.isLivePerformanceEnded) return "—";
      return email || "—";
    },
    inviteAvatarInitial(memberOrOption) {
      if (this.isLivePerformanceEnded) {
        const n = memberOrOption?.nickname || "";
        return n ? String(n[0]).toUpperCase() : "?";
      }
      const em = memberOrOption?.email;
      return em ? String(em[0]).toUpperCase() : "?";
    },
    // 커미션 정보 로드
    async loadCommissionData() {
      try {
        const commissionDoc = await getDoc(doc(firestore, "commissions", this.commissionId));
        if (commissionDoc.exists()) {
          this.commissionData = commissionDoc.data();
        }

        // 폼 정보 로드 (문서 ID = 커미션 ID)
        const formDoc = await getDoc(doc(firestore, "purchaseForm", this.commissionId));
        if (formDoc.exists()) {
          this.formInfo = formDoc.data();
        }
      } catch (err) {
        console.error("커미션 정보 로드 오류:", err);
      }
    },
    // 권한 확인 (commissionData 로드 후 호출해야 함)
    checkAuth() {
      if (!this.userStore.isLogin) {
        this.$alert("로그인이 필요합니다.");
        this.$router.push("/");
        return false;
      }

      // admin이거나 해당 커미션 작성자인 경우 접근 허용
      const isAdmin = this.userStore.auth === "admin";
      const ownerEmail = this.commissionData?.createdBy;
      const isOwner = ownerEmail && ownerEmail === this.userStore.email;

      if (!isAdmin && !isOwner) {
        this.$alert("접근 권한이 없습니다.");
        this.$router.push("/");
        return false;
      }
      return true;
    },
    // 예매 신청 목록 로드
    async loadReservations() {
      try {
        const q = query(
          collection(firestore, "purchaseReservations"),
          where("liveId", "==", this.commissionId)
        );
        const querySnapshot = await getDocs(q);
        this.reservations = querySnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .sort((a, b) => {
            if (!a.createdAt || !b.createdAt) return 0;
            const aTime = a.createdAt.seconds || 0;
            const bTime = b.createdAt.seconds || 0;
            return bTime - aTime; // 최신순
          });
      } catch (err) {
        console.error("예매 목록 로드 오류:", err);
        this.error = "예매 목록을 불러오는 중 오류가 발생했습니다.";
      }
    },
    // 예매 상태 업데이트
    async updateReservationStatus(reservationId, newStatus, reservation = null) {
      try {
        await updateReservationStatusApi(reservationId, newStatus);
        await this.loadReservations();

        // 확정 시 알림톡 발송
        if (newStatus === 'confirmed' && reservation) {
          const phone = (reservation.phone || '').replace(/[^0-9]/g, '');
          if (phone.length >= 9) {
            const ticketNo = this.getTicketNo(reservationId);
            const liveDate = this.commissionData?.date || '';
            const liveName = this.commissionData?.name || reservation.liveTitle || '';
            const notice = '입장 시 링꾹 QR 확인';

            try {
              await sendAlimtalk({
                type: 'ticket_issued',
                destinations: [
                  {
                    to: phone,
                    replaceWords: {
                      '이름': reservation.name || '',
                      '1': liveName,
                      '2': liveDate,
                      '3': `#${ticketNo}`,
                      '4': notice,
                    },
                  },
                ],
                ref: `confirm-${reservationId}`,
              });
              this.$alert("확정되었습니다.\n티켓 발급 알림톡을 발송했습니다.");
            } catch (alimErr) {
              console.warn("알림톡 발송 실패:", alimErr);
              this.$alert("확정되었습니다.\n(알림톡 발송에 실패했습니다. 연락처를 확인해주세요.)");
            }
          } else {
            this.$alert("확정되었습니다.\n(연락처가 없어 알림톡을 발송하지 못했습니다.)");
          }
        } else {
          this.$alert("상태가 업데이트되었습니다.");
        }
      } catch (err) {
        console.error("상태 업데이트 오류:", err);
        this.$alert("상태 업데이트 중 오류가 발생했습니다.");
      }
    },
    // 입금 확인 상태 토글
    async togglePaymentStatus(reservationId, currentStatus) {
      try {
        await confirmPayment(reservationId, !currentStatus);
        // 목록 새로고침
        await this.loadReservations();
      } catch (err) {
        console.error("입금 상태 업데이트 오류:", err);
        this.$alert("입금 상태 업데이트 중 오류가 발생했습니다.");
      }
    },
    async deleteInviteReservation(reservation) {
      const id = reservation?.id;
      if (!id || !reservation?.isInviteTicket) return;

      const ok = await this.$confirm("초대권을 삭제하시겠습니까?\n삭제 후에는 되돌릴 수 없습니다.");
      if (!ok) return;

      try {
        await deleteReservation(id);
        this.$alert("초대권이 삭제되었습니다.");
        await this.loadReservations();
        if (this.showInviteModal) {
          await this.loadInvitedMembers();
        }
      } catch (err) {
        console.error("초대권 삭제 오류:", err);
        this.$alert("초대권 삭제 중 오류가 발생했습니다.");
      }
    },
    openQrModal() {
      this.showQrModal = true;
      this.qrError = "";
      this.qrManualInput = "";
      // 지원 안 되면 입력 모드로
      if (!this.barcodeSupported) {
        this.qrMode = "manual";
      } else {
        this.qrMode = "camera";
      }
    },
    closeQrModal() {
      this.stopQrScan();
      this.showQrModal = false;
      this.qrError = "";
      this.qrManualInput = "";
    },
    setQrMode(mode) {
      if (mode === "camera" && !this.barcodeSupported) return;
      this.qrError = "";
      this.qrMode = mode;
      if (mode !== "camera") this.stopQrScan();
    },
    detectQrWithCanvas(video) {
      const width = video?.videoWidth || 0;
      const height = video?.videoHeight || 0;
      if (!width || !height) return "";

      if (!this._qrCanvas) {
        this._qrCanvas = document.createElement("canvas");
      }
      const canvas = this._qrCanvas;
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return "";

      ctx.drawImage(video, 0, 0, width, height);
      const imageData = ctx.getImageData(0, 0, width, height);
      const code = jsQR(imageData.data, width, height);
      return code?.data || "";
    },
    async startQrScan() {
      this.qrError = "";
      if (!this.barcodeSupported) {
        this.qrError = "이 브라우저는 QR 스캔을 지원하지 않습니다.";
        return;
      }
      if (this.qrScanning) return;

      try {
        if (typeof window !== "undefined" && "BarcodeDetector" in window) {
          this.qrDetector = new window.BarcodeDetector({ formats: ["qr_code"] });
        }
      } catch (e) {
        this.qrError = "QR 스캔을 초기화하지 못했습니다.";
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
        this.qrStream = stream;
        this.qrScanning = true;

        await this.$nextTick();
        const video = this.$refs.qrVideo;
        if (!video) throw new Error("video ref missing");
        video.srcObject = stream;
        await video.play();

        const scanLoop = async () => {
          if (!this.qrScanning) return;
          try {
            let raw = "";
            if (this.qrDetector) {
              const codes = await this.qrDetector.detect(video);
              if (codes && codes.length) {
                raw = codes[0].rawValue || "";
              }
            } else {
              raw = this.detectQrWithCanvas(video);
            }
            if (raw) {
              this.handleQrDecoded(raw);
              return;
            }
          } catch (e) {
            try {
              const raw = this.detectQrWithCanvas(video);
              if (raw) {
                this.handleQrDecoded(raw);
                return;
              }
            } catch (_) {
              // ignore detect errors per-frame
            }
          }
          requestAnimationFrame(scanLoop);
        };
        requestAnimationFrame(scanLoop);
      } catch (e) {
        this.qrError = "카메라 권한 또는 HTTPS 접속이 필요합니다.";
        this.stopQrScan();
      }
    },
    stopQrScan() {
      this.qrScanning = false;
      try {
        const video = this.$refs.qrVideo;
        if (video) video.srcObject = null;
      } catch (e) {
        // ignore
      }
      if (this.qrStream) {
        try {
          this.qrStream.getTracks().forEach((t) => t.stop());
        } catch (e) {
          // ignore
        }
      }
      this.qrStream = null;
      this.qrDetector = null;
    },
    confirmManualQr() {
      const text = (this.qrManualInput || "").trim();
      if (!text) return;
      this.handleQrDecoded(text);
    },
    parseQrPayload(text) {
      const raw = String(text || "").trim();
      if (!raw) return { reservationId: null, liveId: null, ticketNo: null };

      // URL 형태 (MyReservations에서 생성한 ticket 링크 포함)
      if (raw.startsWith("http")) {
        try {
          const u = new URL(raw);
          const rid = u.searchParams.get("rid");
          const parts = (u.pathname || "").split("/").filter(Boolean);
          const liveId = parts[0] === "ticket" ? parts[1] : null;
          return { reservationId: rid, liveId, ticketNo: null };
        } catch (e) {
          // fallthrough
        }
      }

      // JSON 형태
      if (raw.startsWith("{") && raw.endsWith("}")) {
        try {
          const obj = JSON.parse(raw);
          return {
            reservationId: obj.reservationId || obj.rid || null,
            liveId: obj.liveId || null,
            ticketNo: obj.ticketNo || null,
          };
        } catch (e) {
          // fallthrough
        }
      }

      // 티켓번호(#XXXXXXXX or XXXXXXXX) 우선 인식
      const normalizedTicketNo = raw.replace(/^#/, "").trim().toUpperCase();
      if (/^[A-Z0-9]{8}$/.test(normalizedTicketNo)) {
        return { reservationId: null, liveId: null, ticketNo: normalizedTicketNo };
      }

      // 그 외는 reservationId로 간주
      return { reservationId: raw, liveId: null, ticketNo: null };
    },
    async handleQrDecoded(text) {
      this.qrError = "";
      const { reservationId, ticketNo } = this.parseQrPayload(text);
      if (!reservationId && !ticketNo) {
        this.qrError = "QR 내용을 해석할 수 없습니다.";
        return;
      }

      let target = null;
      if (reservationId) {
        target = this.reservations.find((r) => r.id === reservationId) || null;
      }
      if (!target && ticketNo) {
        const t = String(ticketNo).replace(/^#/, "").trim().toUpperCase();
        target = this.reservations.find((r) => this.getTicketNo(r.id) === t) || null;
      }
      if (!target) {
        this.qrError = "이 커미션의 구매 내역에서 찾을 수 없습니다.";
        return;
      }

      // 초대권은 취소되지 않은 이상 항상 입장 가능
      const isAdmissible =
        target.isInviteTicket
          ? target.status !== "cancelled"
          : target.status === "confirmed";

      // 필터/검색 때문에 안 보일 수 있으니 최소한 보이게 처리
      this.filterStatus = "all";
      this.searchKeyword = "";

      // 펼치기 + 하이라이트 + 스크롤
      const expanded = new Set(this.expandedReservations);
      expanded.add(target.id);
      this.expandedReservations = expanded;
      this.highlightReservationId = target.id;

      this.$nextTick(() => {
        const el = this._reservationElMap?.[target.id];
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });

      setTimeout(() => {
        if (this.highlightReservationId === target.id) this.highlightReservationId = null;
      }, 2500);

      this.stopQrScan();
      this.showQrModal = false;

      // 리워드 내역 HTML 생성
      const rewardSummary = this.getRewardSummary(target);
      const rewardHtml = rewardSummary.length
        ? `<div style="margin-top:14px;padding-top:12px;border-top:1px solid #e9ecef;">
            <div style="font-size:11px;font-weight:700;color:#868e96;letter-spacing:0.5px;margin-bottom:8px;">티켓 패키지</div>
            ${rewardSummary.map((rw) => {
          return `<div style="display:flex;justify-content:space-between;align-items:center;padding:4px 0;font-size:13px;color:#495057;">
                <span>${rw.name}</span>
                <span style="font-weight:600;">${rw.quantity}개 · ${rw.subtotal}</span>
              </div>`;
        }).join("")}
          </div>`
        : "";

      // 굿즈 구매 내역 HTML 생성
      const purchaseSummary = this.getPurchaseSummary(target);
      const purchaseHtml = purchaseSummary.length
        ? `<div style="margin-top:14px;padding-top:12px;border-top:1px solid #e9ecef;">
            <div style="font-size:11px;font-weight:700;color:#868e96;letter-spacing:0.5px;margin-bottom:8px;">추가구매</div>
            ${purchaseSummary.map((g) => {
          const option = g.optionAnswer
            ? `<span style="color:#868e96;"> (${g.optionAnswer})</span>`
            : "";
          return `<div style="display:flex;justify-content:space-between;align-items:center;padding:4px 0;font-size:13px;color:#495057;">
                <span>${g.name}${option}</span>
                <span style="font-weight:600;">${g.quantity}개 · ${g.subtotal}</span>
              </div>`;
        }).join("")}
          </div>`
        : "";

      const inviteBadgeHtml = target.isInviteTicket
        ? `<span style="display:inline-block;margin-left:6px;padding:2px 8px;border-radius:999px;background:linear-gradient(135deg,rgba(124,58,237,0.12),rgba(91,33,182,0.12));color:#6d28d9;font-size:11px;font-weight:700;border:1px solid rgba(124,58,237,0.3);">🎟️ 초대권</span>`
        : "";

      if (isAdmissible) {
        const alreadyCheckedIn = !!target.checkedIn;
        const checkedInBannerHtml = alreadyCheckedIn
          ? `<div style="margin-bottom:12px;padding:8px 12px;border-radius:8px;background:#fff8e1;border:1px solid #ffe082;font-size:13px;color:#f57f17;text-align:center;">⚠️ 이미 입장 처리된 티켓입니다.</div>`
          : "";
        const confirmed = await this.$confirm(
          `${checkedInBannerHtml}
          <div style="text-align:center;margin-bottom:16px;">
            <span style="display:inline-block;padding:6px 20px;border-radius:999px;background:#e8f5e9;color:#2e7d32;font-size:16px;font-weight:700;letter-spacing:0.3px;">✓ 입장 가능</span>
          </div>
          <div style="display:flex;align-items:center;margin-bottom:4px;">
            <span style="font-size:15px;font-weight:700;color:#212529;">${target.name || ""}</span>${inviteBadgeHtml}
          </div>
          <div style="font-size:13px;color:#6c757d;">티켓 ${target.quantity || 0}매</div>
          ${rewardHtml}${purchaseHtml}`,
          { cancelLabel: "닫기", confirmLabel: "입장완료" }
        );
        if (confirmed) {
          await this.markCheckedIn(target.id);
        }
      } else {
        const statusText = this.getStatusText(target.status);
        const reasonMap = {
          cancelled: "예매가 취소된 티켓입니다.",
          refund_requested: "환불 요청 중인 티켓입니다.",
          refund_completed: "환불이 완료된 티켓입니다.",
          pending: "아직 확정되지 않은 티켓입니다.",
        };
        const reasonText = reasonMap[target.status] || `현재 상태: ${statusText}`;
        this.$alert(
          `<div style="text-align:center;margin-bottom:16px;">
            <span style="display:inline-block;padding:6px 20px;border-radius:999px;background:#fce4ec;color:#c62828;font-size:16px;font-weight:700;letter-spacing:0.3px;">✕ 입장 불가</span>
          </div>
          <div style="font-size:15px;font-weight:700;color:#212529;margin-bottom:4px;">${target.name || ""}</div>
          <div style="font-size:13px;color:#6c757d;">티켓 ${target.quantity || 0}매</div>
          <div style="margin-top:10px;padding:8px 12px;border-radius:8px;background:#fff8f8;border:1px solid #ffcdd2;font-size:13px;color:#c62828;">${reasonText}</div>
          ${rewardHtml}${purchaseHtml}`
        );
      }
    },
    // 입장 처리
    async markCheckedIn(reservationId) {
      try {
        await updateDoc(doc(firestore, "purchaseReservations", reservationId), {
          checkedIn: true,
          checkedInAt: serverTimestamp(),
        });
        this.reservations = this.reservations.map((r) =>
          r.id === reservationId ? { ...r, checkedIn: true } : r
        );
        this.$alert("입장 처리가 완료되었습니다.");
      } catch (e) {
        console.error("입장 처리 오류:", e);
        this.$alert("입장 처리 중 오류가 발생했습니다.");
      }
    },
    // 입장 취소
    async unmarkCheckedIn(reservationId) {
      const ok = await this.$confirm("입장 처리를 취소하시겠습니까?");
      if (!ok) return;
      try {
        await updateDoc(doc(firestore, "purchaseReservations", reservationId), {
          checkedIn: false,
          checkedInAt: null,
        });
        this.reservations = this.reservations.map((r) =>
          r.id === reservationId ? { ...r, checkedIn: false } : r
        );
      } catch (e) {
        console.error("입장 취소 오류:", e);
        this.$alert("입장 취소 중 오류가 발생했습니다.");
      }
    },
    // 날짜 포맷팅
    formatDate(timestamp) {
      if (!timestamp) return "";
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString("ko-KR");
    },
    // 상태 텍스트
    getStatusText(status) {
      const statusMap = {
        pending: "대기중",
        confirmed: "확정",
        cancelled: "취소",
        refund_requested: "환불 대기중",
        refund_completed: "환불 완료",
      };
      return statusMap[status] || status;
    },
    getUnifiedStatusKey(r) {
      if (r.status === "refund_completed") return "refund_completed";
      if (r.status === "refund_requested") return "refund_requested";
      if (r.status === "cancelled") return "cancelled";
      if (r.status === "confirmed") return "confirmed";
      if (r.paymentConfirmed) return "pay_confirmed";
      return "pay_pending";
    },
    // 입장 가능 여부 — 초대권은 취소되지 않은 이상, 일반은 확정된 경우에만 입장 가능
    canCheckIn(r) {
      if (!r) return false;
      if (r.isInviteTicket) return r.status !== "cancelled";
      return r.status === "confirmed";
    },
    getUnifiedStatusText(r) {
      if (r.status === "refund_completed") return "환불 완료";
      if (r.status === "refund_requested") return "환불 대기중";
      if (r.status === "cancelled") return "취소됨";
      if (r.status === "confirmed") return "예매확정";
      if (r.paymentConfirmed) return "입금완료";
      return "입금 확인중";
    },
    // 이메일 복사
    async copyEmail(email) {
      try {
        await navigator.clipboard.writeText(email);
        this.$alert("이메일이 복사되었습니다.");
      } catch (err) {
        console.error("복사 오류:", err);
        this.$alert("복사에 실패했습니다.");
      }
    },
    async copyText(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.$alert("복사되었습니다.");
      } catch (err) {
        console.error("복사 오류:", err);
        this.$alert("복사에 실패했습니다.");
      }
    },
    formatPhone(phone) {
      const p = String(phone || "").replace(/[^0-9]/g, "");
      if (p.length === 11) return p.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
      if (p.length === 10) return p.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
      return phone;
    },
    // 리워드 요약
    getRewardSummary(reservation) {
      const tiers = this.formInfo?.rewardTiers;
      const answers = reservation?.rewardAnswers;
      if (!Array.isArray(tiers) || !tiers.length || !Array.isArray(answers)) return [];
      return answers
        .map(a => {
          const tier = tiers[a.tierIndex];
          if (!tier || !a.quantity) return null;
          return {
            name: tier.name,
            quantity: a.quantity,
            subtotal: this.formatAmount((Number(tier.price) || 0) * a.quantity),
          };
        })
        .filter(Boolean);
    },
    // 추가구매항목 요약
    getPurchaseSummary(reservation) {
      const items = this.formInfo?.purchaseItems;
      const answers = reservation?.purchaseAnswers;
      if (!Array.isArray(items) || !items.length || !Array.isArray(answers)) return [];
      return items
        .map((item, i) => {
          const ans = answers[i];
          const qty = ans?.quantity || 0;
          if (!qty) return null;
          const subtotal = this.formatAmount((Number(item.price) || 0) * qty);
          let optionAnswer = null;
          if (ans?.optionAnswer) {
            optionAnswer = Array.isArray(ans.optionAnswer)
              ? ans.optionAnswer.join(", ")
              : String(ans.optionAnswer);
          }
          return { name: item.name, quantity: qty, subtotal, optionAnswer };
        })
        .filter(Boolean);
    },
    // 커스텀 필드 답변이 있는지 확인
    hasCustomFieldAnswers(reservation) {
      return reservation.customFieldAnswers &&
        this.formInfo?.customFields &&
        Object.keys(reservation.customFieldAnswers).length > 0;
    },
    // 커스텀 필드 답변 가져오기
    getCustomFieldAnswers(reservation) {
      if (!this.formInfo?.customFields || !reservation.customFieldAnswers) {
        return [];
      }

      return this.formInfo.customFields.map((field, index) => {
        const answer = reservation.customFieldAnswers[index];
        let displayAnswer = '';

        if (Array.isArray(answer)) {
          displayAnswer = answer.join(', ');
        } else if (answer !== undefined && answer !== null) {
          displayAnswer = String(answer);
        } else {
          displayAnswer = '-';
        }

        return {
          question: field.title,
          answer: displayAnswer
        };
      }).filter(item => item.answer !== '-');
    },
    // 정렬 토글
    toggleSort() {
      const sortOptions = ["dateDesc", "nameAsc", "quantityDesc"];
      const currentIndex = sortOptions.indexOf(this.sortBy);
      const nextIndex = (currentIndex + 1) % sortOptions.length;
      this.sortBy = sortOptions[nextIndex];
    },
    // 엑셀(CSV) 다운로드
    downloadExcel() {
      const list = this.filteredReservations || [];
      if (!list.length) {
        this.$alert("내보낼 데이터가 없습니다.");
        return;
      }

      const safeCell = (value) => {
        const str = value === null || value === undefined ? "" : String(value);
        // Excel formula injection 방지
        if (/^[=+\-@]/.test(str)) return `'${str}`;
        return str;
      };

      const csvEscape = (value) => {
        const str = safeCell(value);
        const escaped = str.replace(/"/g, '""');
        if (/[",\r\n]/.test(escaped)) return `"${escaped}"`;
        return escaped;
      };

      const baseHeaders = ["신청일시", "이름", "이메일", "매수", "금액", "패키지합계", "추가구매합계", "상태", "입금"];
      const rawTitles = (this.formInfo?.customFields || [])
        .map((f) => (f?.title || "").trim())
        .filter(Boolean);
      const rewardTierNames = (this.formInfo?.rewardTiers || [])
        .map((t) => (t?.name || "").trim())
        .filter(Boolean);
      const purchaseItemTitles = (this.formInfo?.purchaseItems || [])
        .map((item) => (item?.name || "").trim())
        .filter(Boolean);

      // 중복 타이틀 방지
      const titleCounts = new Map();
      const customHeaders = rawTitles.map((t) => {
        const prev = titleCounts.get(t) || 0;
        titleCounts.set(t, prev + 1);
        return prev === 0 ? t : `${t}(${prev + 1})`;
      });
      const rewardCounts = new Map();
      const rewardHeaders = rewardTierNames.map((t) => {
        const prev = rewardCounts.get(t) || 0;
        rewardCounts.set(t, prev + 1);
        const label = prev === 0 ? t : `${t}(${prev + 1})`;
        return `패키지:${label}`;
      });
      const purchaseCounts = new Map();
      const purchaseHeaders = purchaseItemTitles.map((t) => {
        const prev = purchaseCounts.get(t) || 0;
        purchaseCounts.set(t, prev + 1);
        const label = prev === 0 ? t : `${t}(${prev + 1})`;
        return `굿즈:${label}`;
      });

      const headers = [...baseHeaders, ...customHeaders, ...rewardHeaders, ...purchaseHeaders];

      const rows = list.map((r) => {
        const values = [];
        values.push(this.formatDate(r.createdAt));
        values.push(r.name || "");
        values.push(this.isLivePerformanceEnded ? "—" : (r.email || ""));
        values.push(r.quantity ?? "");
        values.push(this.formatAmountForCSV(r.totalAmount));
        values.push(this.formatAmountForCSV(r.rewardTotal));
        values.push(this.formatAmountForCSV(r.purchaseTotal));
        values.push(this.getStatusText(r.status));
        values.push(r.paymentConfirmed ? "입금완료" : "입금대기");

        // 커스텀 필드 (인덱스 기반)
        const answers = r.customFieldAnswers || [];
        for (let i = 0; i < rawTitles.length; i += 1) {
          const v = answers[i] ?? answers[String(i)];
          if (Array.isArray(v)) values.push(v.join(", "));
          else if (v === null || v === undefined) values.push("");
          else values.push(String(v));
        }

        // 리워드 (수량)
        const rewardAnswers = r.rewardAnswers || [];
        const rewardTiers = this.formInfo?.rewardTiers || [];
        for (let i = 0; i < rewardTierNames.length; i += 1) {
          const ra = rewardAnswers.find(a => a.tierIndex === i);
          if (!ra || !ra.quantity) { values.push(""); continue; }
          const tier = rewardTiers[i];
          const price = (Number(tier?.price) || 0) * ra.quantity;
          values.push(`${ra.quantity}개(${this.formatAmountForCSV(price)}원)`);
        }

        // 추가구매항목 (수량, 옵션)
        const purchaseAnswers = r.purchaseAnswers || [];
        const purchaseItems = this.formInfo?.purchaseItems || [];
        for (let i = 0; i < purchaseItemTitles.length; i += 1) {
          const ans = purchaseAnswers[i];
          const qty = ans?.quantity || 0;
          if (!qty) { values.push(""); continue; }
          const item = purchaseItems[i];
          const price = (Number(item?.price) || 0) * qty;
          let cell = `${qty}개(${this.formatAmountForCSV(price)}원)`;
          if (ans?.optionAnswer) {
            const opt = Array.isArray(ans.optionAnswer) ? ans.optionAnswer.join(", ") : String(ans.optionAnswer);
            if (opt) cell += ` / ${opt}`;
          }
          values.push(cell);
        }
        return values;
      });

      const csvLines = [
        headers.map(csvEscape).join(","),
        ...rows.map((row) => row.map(csvEscape).join(",")),
      ];

      // UTF-8 BOM 추가(엑셀 한글 깨짐 방지)
      const csv = `\uFEFF${csvLines.join("\r\n")}`;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const fileName = `구매신청_${this.commissionId || "export"}_${new Date().toISOString().slice(0, 10)}.csv`;

      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
    formatAmount(amount) {
      if (typeof amount === 'number') {
        return `${new Intl.NumberFormat('ko-KR').format(amount)}원`;
      }
      // 이미 포맷된 문자열이면 그대로 반환
      return amount || '-';
    },
    formatAmountForCSV(amount) {
      if (typeof amount === 'number') {
        return new Intl.NumberFormat('ko-KR').format(amount);
      }
      return amount || "";
    },
    // 정렬 버튼 텍스트 반환
    getSortButtonText() {
      switch (this.sortBy) {
        case "dateDesc":
          return "최신순";
        case "nameAsc":
          return "이름순";
        case "quantityDesc":
          return "구매순";
        default:
          return "최신순";
      }
    },

    // 초대권 모달 열기
    openInviteModal() {
      this.showInviteModal = true;
      this.inviteSearchQuery = "";
      this.inviteFilteredUsers = [];
      this.inviteSelectedUser = null;
      this.inviteLoading = false;
      this.loadInvitedMembers();
    },
    // 초대권 모달 닫기
    closeInviteModal() {
      this.showInviteModal = false;
      this.inviteSearchQuery = "";
      this.inviteFilteredUsers = [];
      this.inviteSelectedUser = null;
    },
    // 이미 발급된 초대권 목록 로드
    async loadInvitedMembers() {
      try {
        const invited = this.reservations.filter((r) => r.isInviteTicket);
        const membersInfo = [];

        for (const reservation of invited) {
          try {
            const usersRef = collection(firestore, "users");
            const q = query(usersRef, where("email", "==", reservation.email));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
              const userData = querySnapshot.docs[0].data();
              let profileImageUrl = null;

              if (userData.img) {
                try {
                  const imageRef = storageRef(storage, `profile/${userData.img}`);
                  profileImageUrl = await getDownloadURL(imageRef);
                } catch (error) {
                  console.warn(`프로필 이미지 URL 생성 실패 (${reservation.email}):`, error);
                  profileImageUrl = await this.resolveUserProfileImage(userData.img);
                }
              }

              membersInfo.push({
                email: reservation.email,
                nickname: userData.nickname || reservation.name || reservation.regNickname || null,
                profileImage: profileImageUrl,
                reservationId: reservation.id,
              });
            } else {
              membersInfo.push({
                email: reservation.email,
                nickname: reservation.name || reservation.regNickname || null,
                profileImage: null,
                reservationId: reservation.id,
              });
            }
          } catch (error) {
            console.error(`초대권 사용자 정보 조회 오류 (${reservation.email}):`, error);
            membersInfo.push({
              email: reservation.email,
              nickname: reservation.name || reservation.regNickname || null,
              profileImage: null,
              reservationId: reservation.id,
            });
          }
        }

        this.invitedMembers = membersInfo;
      } catch (err) {
        console.error("초대권 목록 로드 오류:", err);
      }
    },
    // 사용자 검색 (AutoComplete)
    async searchInviteUsers(event) {
      const searchTerm = (event.query || "").trim().toLowerCase();
      if (!searchTerm) {
        this.inviteFilteredUsers = [];
        return;
      }
      try {
        const usersRef = collection(firestore, "users");
        const allUsersSnapshot = await getDocs(usersRef);
        const results = [];

        for (const docSnap of allUsersSnapshot.docs) {
          const userData = docSnap.data();
          const email = (userData.email || "").toLowerCase();
          const nickname = (userData.nickname || "").toLowerCase();

          const matched = this.isLivePerformanceEnded
            ? nickname.includes(searchTerm)
            : email.includes(searchTerm) || nickname.includes(searchTerm);
          if (matched) {
            let profileImageUrl = null;

            if (userData.img) {
              try {
                const imageRef = storageRef(storage, `profile/${userData.img}`);
                profileImageUrl = await getDownloadURL(imageRef);
              } catch (error) {
                console.warn("프로필 이미지 로드 실패:", error);
                profileImageUrl = await this.resolveUserProfileImage(userData.img);
              }
            }

            const label = this.isLivePerformanceEnded
              ? `${userData.nickname || "닉네임 없음"}`
              : `${userData.nickname || "닉네임 없음"} (${userData.email})`;

            results.push({
              email: userData.email,
              nickname: userData.nickname || null,
              uid: userData.uid || null,
              profileImage: profileImageUrl,
              label,
            });
          }
        }
        this.inviteFilteredUsers = results.slice(0, 10);
      } catch (err) {
        console.error("사용자 검색 오류:", err);
        this.inviteFilteredUsers = [];
      }
    },
    // 사용자 선택
    selectInviteUser(event) {
      this.inviteSelectedUser = event.value;
      this.inviteSearchQuery = event.value.label;
    },
    // 초대권 등록
    async registerInviteTicket() {
      if (!this.inviteSelectedUser) {
        this.$alert("사용자를 선택해주세요.");
        return;
      }

      // 중복 체크 (같은 공연 + 같은 유저에게 이미 초대권 발급 여부)
      const alreadyInvited = this.reservations.some(
        (r) => r.isInviteTicket && r.email === this.inviteSelectedUser.email && r.status !== "cancelled"
      );
      if (alreadyInvited) {
        this.$alert("이미 초대권이 발급된 사용자입니다.");
        return;
      }

      try {
        this.inviteLoading = true;

        const user = this.inviteSelectedUser;
        const reservationData = {
          liveId: this.commissionId,
          liveTitle: this.commissionData?.name || "",
          name: user.nickname || user.email?.split("@")[0] || "",
          email: user.email || "",
          phone: "",
          quantity: 1,
          userId: user.uid || null,
          regId: user.email || null,
          regNickname: user.nickname || null,
          customFieldAnswers: {},
          purchaseAnswers: [],
          ticketAmount: 0,
          purchaseTotal: 0,
          totalAmount: 0,
          isInviteTicket: true,
          status: "confirmed",
          paymentConfirmed: true,
          invitedBy: this.userStore.email || null,
        };

        const newId = await createReservation(reservationData);

        // 즉시 확정 처리 (백엔드가 status 필드를 생성 시 처리하지 않는 경우 대비)
        if (newId) {
          try {
            await updateReservationStatusApi(newId, "confirmed");
            await confirmPayment(newId, true);
          } catch (_) { /* 이미 확정 처리된 경우 무시 */ }
        }

        // 초대 받은 사용자에게 알림 발송
        if (user.email) {
          try {
            await createNotification({
              userId: user.email,
              type: "ticket",
              title: "무료 초대권이 발급되었습니다 🎟️",
              message: `'${this.commissionData?.name || "커미션"}'에 대한 무료 초대권이 발급되었습니다. 예매 내역에서 QR 코드를 확인하세요.`,
              liveId: this.commissionId,
              read: false,
            });
          } catch (notifErr) {
            console.warn("초대권 알림 발송 실패:", notifErr);
          }
        }

        await this.loadReservations();
        await this.loadInvitedMembers();

        this.inviteSearchQuery = "";
        this.inviteSelectedUser = null;
        this.inviteFilteredUsers = [];
        this.$alert(`${user.nickname || user.email}님께 초대권이 발급되었습니다.`);
      } catch (err) {
        console.error("초대권 등록 오류:", err);
        this.$alert("초대권 등록 중 오류가 발생했습니다.");
      } finally {
        this.inviteLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ========================================
   481px 이상 (기본)
   ======================================== */
.reservation-container {
  max-width: 95%;
  margin: 0 auto;
  padding: 24px 0;
  font-family: "NanumSquareRoundR", sans-serif;
  background: #ffffff;
  min-height: 100vh;
}

.reservation-container-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e0e0e0;
  gap: 8px;
  flex-wrap: wrap;
  margin-left: auto;
  margin-right: auto;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.info-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f0f6ff;
  border: 1px solid #c8deff;
  border-radius: 8px;
  padding: 10px 14px;
  margin: 12px 0 4px;
  font-size: 13px;
  color: #3a5ea8;
  line-height: 1.5;
}

.info-banner-icon {
  font-size: 15px;
  flex-shrink: 0;
}

.reservation-container-header h1 {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 20px;
  font-weight: bold;
  color: #333;
  margin: 0;
  letter-spacing: -0.5px;
}

.header-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.qr-btn {
  padding: 6px 12px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #333;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundB", sans-serif;
  white-space: nowrap;
}

.qr-btn:hover:not(:disabled) {
  border-color: #66bdff;
  color: #2b8bd6;
  background: rgba(102, 189, 255, 0.12);
}

.qr-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.excel-btn {
  padding: 6px 12px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #333;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundB", sans-serif;
  white-space: nowrap;
}

.excel-btn:hover:not(:disabled) {
  border-color: #66bdff;
  color: #2b8bd6;
  background: rgba(102, 189, 255, 0.12);
}

.excel-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.qr-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.qr-modal {
  width: 100%;
  max-width: 520px;
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.qr-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #e9ecef;
}

.qr-modal-title {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 16px;
  color: #333;
}

.qr-modal-close {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 16px;
  padding: 6px 8px;
  border-radius: 8px;
  color: #666;
}

.qr-modal-close:hover {
  background: #f8f9fa;
  color: #333;
}

.qr-modal-body {
  padding: 16px;
}

.qr-mode-row {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.qr-mode-btn {
  flex: 1;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #e9ecef;
  background: #fff;
  cursor: pointer;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 13px;
  color: #666;
}

.qr-mode-btn.active {
  border-color: rgba(102, 189, 255, 0.75);
  background: rgba(102, 189, 255, 0.12);
  color: #2b8bd6;
}

.qr-mode-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.qr-camera-box,
.qr-manual-box {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.qr-video {
  width: 100%;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  background: #111;
  aspect-ratio: 4 / 3;
}

.qr-camera-actions {
  display: flex;
  gap: 8px;
}

.qr-action-btn {
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(102, 189, 255, 0.75);
  background: rgba(102, 189, 255, 0.12);
  color: #2b8bd6;
  cursor: pointer;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 13px;
}

.qr-action-btn.subtle {
  border-color: #e9ecef;
  background: #fff;
  color: #666;
}

.qr-action-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.qr-input {
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid #e9ecef;
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 13px;
}

.qr-input:focus {
  outline: none;
  border-color: rgba(102, 189, 255, 0.75);
}

.qr-hint {
  font-size: 12px;
  color: #868e96;
  font-family: "NanumSquareRoundR", sans-serif;
  margin-top: 10px;
}

.qr-error {
  margin-top: 10px;
  color: #c62828;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 13px;
}

.reservation-card.qr-highlight {
  border-color: rgba(102, 189, 255, 0.9);
  background: rgba(102, 189, 255, 0.06);
}

.back-btn {
  display: flex;
  align-items: center;
  margin-top: 3px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 22px;
  color: #333;
  padding: 4px;
  transition: all 0.2s ease;
}

.back-btn:hover {
  color: #66bdff;
  transform: translateX(-2px);
}

.back-btn:active {
  color: #4a9eff;
}

.loading,
.error {
  text-align: center;
  padding: 60px 20px;
  font-size: 18px;
  animation: fadeIn 0.4s ease-out;
}

.error {
  color: #e74c3c;
  background: rgba(231, 76, 60, 0.1);
  border-radius: 16px;
  padding: 30px;
}

/* 공연 정보 섹션 */
.info-section {
  padding: 32px 0;
  margin-bottom: 0;
  border-bottom: 1px solid #e9ecef;
  animation: fadeIn 0.5s ease-out;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
}

.info-section:last-of-type {
  border-bottom: none;
}

.payment-info-section {
  background: none;
  border: none;
  border-bottom: 1px solid #e9ecef;
}

.payment-info-box {
  background: white;
  padding: 0;
  border-radius: 0;
  border: none;
}

.account-number {
  font-family: "NanumSquareRoundB", sans-serif;
  letter-spacing: 0.5px;
  color: #1e40af;
}

.info-section h2 {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px 0;
  padding-bottom: 0;
  border-bottom: none;
  position: relative;
  display: block;
  letter-spacing: -0.5px;
}

.info-section h2::before {
  display: none;
}

.section-description {
  font-size: 14px;
  color: #666;
  margin: 0 0 24px 0;
  font-family: "NanumSquareRoundR", sans-serif;
}

.live-info-content {
  background: white;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e9ecef;
  margin-top: 16px;
}

.info-row {
  display: flex;
  padding: 12px 0;
  border-bottom: 1px solid #f1f3f5;
  transition: none;
}

.info-row:hover {
  background: none;
  margin: 0;
  padding: 12px 0;
  border-radius: 0;
}

.info-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.info-row .label {
  flex: 0 0 120px;
  font-family: "NanumSquareRoundB", sans-serif;
  color: #495057;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0;
}

.info-row .value {
  flex: 1;
  color: #212529;
  font-weight: normal;
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  letter-spacing: 0;
}

/* 통계 섹션 */
.stats-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  padding: 32px 0;
  margin-bottom: 0;
  border-bottom: 1px solid #e9ecef;
  animation: fadeIn 0.5s ease-out 0.1s backwards;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
}

.stat-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  box-shadow: none;
  border: 1px solid #e9ecef;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  display: none;
}

.stat-card:hover {
  transform: none;
  box-shadow: none;
  border-color: #66bdff;
}

.stat-label {
  font-size: 13px;
  color: #718096;
  margin-bottom: 8px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-weight: 600;
  letter-spacing: 0;
}

.stat-value {
  font-size: 26px;
  font-weight: 700;
  color: #2d3748;
  font-family: "NanumSquareRoundB", sans-serif;
  letter-spacing: 0;
}

.stat-value.pending {
  color: #f59e0b;
}

.stat-value.confirmed {
  color: #10b981;
}

.stat-value.cancelled {
  color: #ef4444;
}

/* 목록 섹션 */
.list-section {
  background: white;
  border-radius: 0;
  padding: 12px 0;
  box-shadow: none;
  border: none;
  animation: fadeIn 0.5s ease-out 0.2s backwards;
  transition: none;
  margin-left: auto;
  margin-right: auto;
}

.list-section:hover {
  box-shadow: none;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 16px;
  flex-wrap: wrap;
}

.list-header h2 {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0;
  display: block;
  letter-spacing: -0.5px;
  flex-shrink: 0;
}

.list-header h2::before {
  display: none;
}

.ticket-count-info {
  font-size: 12px;
  color: #868e96;
  font-weight: 400;
  font-family: "NanumSquareRoundR", sans-serif;
  margin-left: 8px;
  letter-spacing: 0;
}

.ticket-count-checkedin {
  font-size: 12px;
  color: #868e96;
  font-weight: 400;
  font-family: "NanumSquareRoundR", sans-serif;
  margin-left: 8px;
  letter-spacing: 0;
}

.status-filter {
  display: flex;
  gap: 8px;
  align-items: center;
  overflow-x: auto;
  white-space: nowrap;
  scrollbar-width: none;
  -ms-overflow-style: none;
  min-width: 0;
}

.status-filter::-webkit-scrollbar {
  display: none;
}

.controls {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  margin-bottom: 14px;
}

.search-sort-row {
  display: flex;
  gap: 8px;
  width: 100%;
}

.search-box {
  flex: 1;
  min-width: 100px;
}

.search-input {
  width: 100%;
  height: 34px !important;
  padding: 0 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.875rem;
  font-family: "NanumSquareRoundR", sans-serif;
  color: #333;
  transition: border-color 0.2s ease;
}

.search-input::placeholder {
  color: #a0aec0;
}

.search-input:focus {
  outline: none;
  border-color: #66bdff;
}

.sort-box {
  flex-shrink: 0;
}

.sort-button {
  height: 34px;
  padding: 8px 12px;
  background: #66bdff;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  font-family: "NanumSquareRoundB", sans-serif;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.sort-button:hover {
  background: #4a9eff;
}

.sort-button:active {
  background: #3a8eef;
}

.sort-icon {
  width: 16px;
  height: 16px;
  filter: brightness(0) invert(1);
}

.filter-btn {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 10px;
  background: #fff;
  color: #666;
  font-size: 0.875rem;
  font-family: "NanumSquareRoundB", sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.filter-btn:hover {
  border-color: #66bdff;
  color: #66bdff;
}

.filter-btn.selected {
  background: #66bdff;
  border-color: #66bdff;
  color: #fff;
}

.reservations-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* 집계 카드 */
.summary-stats-card {
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  justify-content: flex-end;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 2px solid #ceeaff;
  border-radius: 8px;
  padding: 12px 20px;
  margin-top: 4px;
  box-shadow: 0 2px 8px rgba(102, 189, 255, 0.15);
  min-height: 56px;
}

.summary-stats-content {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "NanumSquareRoundEB", sans-serif;
  text-align: right;
}

.stats-label {
  font-size: 14px;
  font-weight: 500;
  color: #999;
  letter-spacing: -0.3px;
  font-family: "NanumSquareRoundR", sans-serif;
}

.stats-total {
  font-size: 20px;
  font-weight: bold;
  color: #2b8bd6;
  letter-spacing: -0.5px;
}

.stats-breakdown {
  font-size: 14px;
  font-weight: 500;
  color: #666;
  letter-spacing: -0.3px;
  font-family: "NanumSquareRoundR", sans-serif;
}

.summary-stats-extra {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #d7eaf8;
  min-width: 280px;
}

.stats-extra-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 13px;
  color: #4a5568;
}

.stats-extra-row+.stats-extra-row {
  margin-top: 6px;
}

.stats-extra-label {
  color: #667085;
}

.stats-extra-value {
  color: #1f2937;
  text-align: right;
}

.stats-extra-detail .stats-extra-value {
  color: #2b8bd6;
}

.no-data {
  text-align: center;
  padding: 40px;
  color: #a0aec0;
  background: white;
  border-radius: 8px;
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  border: 1px solid #e9ecef;
  letter-spacing: 0;
}

.reservation-card {
  background: white;
  border-radius: 8px;
  box-shadow: none;
  border: 1px solid #e9ecef;
  transition: all 0.2s ease;
  overflow: hidden;
}

.reservation-card:hover {
  box-shadow: none;
  border-color: #66bdff;
  transform: none;
}

/* 리스트 요약 */
.reservation-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.summary-main {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.summary-name {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  min-width: 40px;
  letter-spacing: 0;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.summary-email {
  font-size: 12px;
  color: #718096;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  letter-spacing: 0;
}

.summary-phone {
  font-size: 12px;
  color: #718096;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  letter-spacing: 0;
}

.summary-quantity {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #66bdff;
  min-width: 40px;
  text-align: center;
  letter-spacing: 0;
}

.summary-amount {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #2b8bd6;
  min-width: 60px;
  text-align: right;
  letter-spacing: 0;
  margin-right: 10px;
}

.summary-status {
  display: flex;
  align-items: center;
  gap: 8px;
}

.payment-badge {
  padding: 6px 14px;
  border-radius: 10px;
  font-size: 12px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-weight: 600;
  background: white;
  color: #718096;
  border: 1px solid #ddd;
  letter-spacing: 0;
  transition: all 0.2s ease;
}

.payment-badge.confirmed {
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  color: #155724;
  border: 1px solid #10b981;
  box-shadow: none;
}

.invite-ticket-label {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  background: linear-gradient(135deg, rgba(124, 58, 237, 0.12) 0%, rgba(91, 33, 182, 0.12) 100%);
  color: #6d28d9;
  border: 1px solid rgba(124, 58, 237, 0.3);
  border-radius: 20px;
  font-size: 11px;
  font-family: "NanumSquareRoundB", sans-serif;
  white-space: nowrap;
}

.checked-in-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  background: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #a5d6a7;
  border-radius: 20px;
  font-size: 11px;
  font-family: "NanumSquareRoundB", sans-serif;
  white-space: nowrap;
}

.not-checked-in-badge {
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-weight: 600;
  letter-spacing: -0.2px;
  transition: none;
  border: 1px solid #dee2e6;
  background: #f8f9fa;
  color: #6c757d;
  white-space: nowrap;
}

.expand-icon {
  font-size: 11px;
  color: #66bdff;
  min-width: 18px;
  text-align: center;
  transition: transform 0.2s ease;
}

/* 상세 뷰 */
.reservation-detail {
  border-top: 1px solid #e9ecef;
  padding: 24px;
  background: #fff;
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    max-height: 0;
  }

  to {
    opacity: 1;
    max-height: 500px;
  }
}

.detail-section {
  margin-bottom: 20px;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.detail-row {
  display: flex;
  align-items: flex-start;
  padding: 12px 0;
  gap: 16px;
}

.detail-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.custom-answers-section {
  margin-top: 20px;
}

.custom-answers-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 6px 0 18px;
}

.custom-answers-title::before,
.custom-answers-title::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #e9ecef;
}

.custom-answers-title span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid #e9ecef;
  background: #fff;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #495057;
  letter-spacing: -0.2px;
  white-space: nowrap;
}

.detail-label {
  font-family: "NanumSquareRoundB", sans-serif;
  color: #333;
  font-size: 13px;
  font-weight: 600;
  min-width: 90px;
  letter-spacing: -0.2px;
  flex-shrink: 0;
  line-height: 1.5;
}

.detail-value {
  color: #6c757d;
  font-size: 14px;
  letter-spacing: -0.2px;
  line-height: 1.6;
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.detail-value-with-action {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex: 1;
  color: #6c757d;
  font-size: 14px;
  letter-spacing: -0.2px;
  min-width: 0;
}

.detail-value-with-action>span {
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.copy-btn-small {
  padding: 5px 10px;
  background: #66bdff;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  transition: all 0.2s ease;
}

.copy-btn-small:hover {
  background: #4a9eff;
}

.copy-btn-small:active {
  background: #3a8eef;
}

.copy-btn-icon {
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.copy-btn-icon:hover {
  background: #e9ecef;
}

.copy-btn-icon:active {
  background: #dee2e6;
}

.copy-btn-icon img {
  width: 16px;
  height: 16px;
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

.copy-btn-icon:hover img {
  opacity: 0.9;
}

.reservation-status {
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-weight: 600;
  letter-spacing: -0.2px;
  transition: none;
  border: 1px solid transparent;
}

.reservation-status.pending {
  background: #fff9e6;
  color: #996c00;
  border-color: #ffe8a1;
}

.reservation-status.confirmed {
  background: #e8f5e9;
  color: #2e7d32;
  border-color: #a5d6a7;
}

.reservation-status.cancelled {
  background: #fce4ec;
  color: #c62828;
  border-color: #f8bbd0;
}

.reservation-status.refund_requested {
  background: rgba(102, 189, 255, 0.1);
  color: #1a6fa8;
  border-color: rgba(102, 189, 255, 0.4);
}

.reservation-status.refund_completed {
  background: rgba(111, 66, 193, 0.1);
  color: #5a32a3;
  border-color: rgba(111, 66, 193, 0.35);
}

.reservation-status.pay_confirmed {
  background: rgba(102, 187, 106, 0.12);
  color: #2e7d32;
  border-color: rgba(102, 187, 106, 0.35);
}

.reservation-status.pay_pending {
  background: #f8f9fa;
  color: #868e96;
  border-color: #e9ecef;
}

.refund-completed-badge {
  width: 100%;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid rgba(57, 73, 171, 0.3);
  background: linear-gradient(135deg, rgba(57, 73, 171, 0.06) 0%, rgba(121, 134, 203, 0.08) 100%);
  color: #3949ab;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 13px;
  text-align: center;
  letter-spacing: 0.5px;
}

.purchase-option-text {
  font-size: 12px;
  color: #868e96;
}

.purchase-total-row {
  border-top: 1px solid #e9ecef;
  margin-top: 4px;
  padding-top: 12px;
}

.purchase-total-value {
  font-family: "NanumSquareRoundB", sans-serif;
  color: #1a6fa8;
}

.reservation-date {
  font-size: 12px;
  color: #718096;
  font-family: "NanumSquareRoundR", sans-serif;
}

.reservation-info {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
  padding: 14px;
  background: white;
  border-radius: 8px;
}

.info-item {
  display: flex;
  gap: 12px;
  font-size: 14px;
}

.item-label {
  flex: 0 0 100px;
  font-family: "NanumSquareRoundB", sans-serif;
  color: #66bdff;
  font-size: 14px;
}

.info-item span:not(.item-label) {
  flex: 1;
  word-break: break-word;
  overflow-wrap: break-word;
  color: #2d3748;
}

.multiline {
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: break-word;
  color: #2d3748;
  font-size: 14px;
}

.payment-btn {
  padding: 10px 24px;
  border: 1px solid #66bdff;
  border-radius: 8px;
  background: #ffffff;
  color: #2b8bd6;
  font-size: 14px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: -0.2px;
}

.payment-btn:hover {
  background: rgba(102, 189, 255, 0.12);
}

.payment-btn:active {
  background: rgba(102, 189, 255, 0.2);
}

.payment-btn.confirmed {
  border-color: #66bb6a;
  color: #2e7d32;
}

.payment-btn.confirmed:hover {
  background: rgba(102, 187, 106, 0.12);
}

.payment-btn.confirmed:active {
  background: rgba(102, 187, 106, 0.2);
}

.reservation-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
}

.btn-confirm,
.btn-pending,
.btn-refund-complete,
.btn-cancel,
.btn-uncheck-in,
.btn-check-in {
  padding: 10px 24px;
  border: 1px solid transparent;
  border-radius: 8px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  letter-spacing: -0.2px;
  transition: all 0.2s ease;
}

.btn-confirm {
  background: #ffffff;
  color: #2e7d32;
  border-color: rgba(102, 187, 106, 0.55);
}

.btn-confirm:hover {
  background: rgba(102, 187, 106, 0.12);
}

.btn-confirm:active {
  background: rgba(102, 187, 106, 0.2);
}

.btn-pending {
  background: #ffffff;
  color: #b26a00;
  border-color: rgba(255, 167, 38, 0.55);
}

.btn-pending:hover {
  background: rgba(255, 167, 38, 0.12);
}

.btn-pending:active {
  background: rgba(255, 167, 38, 0.2);
}

.btn-refund-complete {
  background: #ffffff;
  color: #5a32a3;
  border-color: rgba(111, 66, 193, 0.45);
}

.btn-refund-complete:hover {
  background: rgba(111, 66, 193, 0.1);
}

.btn-refund-complete:active {
  background: rgba(111, 66, 193, 0.18);
}

.btn-cancel {
  background: #ffffff;
  color: #c62828;
  border-color: rgba(239, 83, 80, 0.55);
}

.btn-cancel:hover {
  background: rgba(239, 83, 80, 0.12);
}

.btn-cancel:active {
  background: rgba(239, 83, 80, 0.2);
}

.btn-uncheck-in {
  background: #ffffff;
  color: #546e7a;
  border-color: rgba(84, 110, 122, 0.45);
}

.btn-uncheck-in:hover {
  background: rgba(84, 110, 122, 0.1);
}

.btn-uncheck-in:active {
  background: rgba(84, 110, 122, 0.18);
}

.btn-check-in {
  background: #2e7d32;
  color: #ffffff;
  border-color: #2e7d32;
}

.btn-check-in:hover {
  background: #266a2b;
  border-color: #266a2b;
}

.btn-check-in:active {
  background: #1f5e26;
  border-color: #1f5e26;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ========================================
   480px 이하
   ======================================== */
@media (max-width: 480px) {
  .reservation-container {
    max-width: 100%;
    padding: 16px 0;
  }

  .reservation-container-header {
    margin-bottom: 16px;
    padding-bottom: 6px;
    padding-left: 12px;
    padding-right: 12px;
  }

  .reservation-container-header h1 {
    font-size: 18px;
    letter-spacing: -0.5px;
  }

  .back-btn {
    font-size: 20px;
    padding: 4px;
  }

  .header-spacer {
    width: 20px;
  }

  .excel-btn {
    padding: 6px 10px;
    font-size: 12px;
    border-radius: 8px;
  }

  .qr-btn {
    padding: 6px 10px;
    font-size: 12px;
    border-radius: 8px;
  }

  .loading,
  .error {
    padding: 30px 12px;
    font-size: 13px;
  }

  .error {
    padding: 16px 12px;
    border-radius: 10px;
  }

  .info-section {
    padding: 24px 12px;
  }

  .payment-info-box {
    padding: 0;
  }

  .account-number {
    font-size: 13px;
  }

  .info-section h2 {
    font-size: 16px;
    margin-bottom: 6px;
    letter-spacing: -0.5px;
  }

  .section-description {
    margin-bottom: 16px;
    font-size: 13px;
  }

  .live-info-content {
    padding: 16px;
    border-radius: 8px;
  }


  .info-banner {
    margin: 12px 12px 4px;
  }

  .info-row {
    flex-direction: column;
    gap: 4px;
    padding: 10px 0;
  }

  .info-row:hover {
    margin: 0;
    padding: 10px 0;
  }

  .info-row .label {
    flex: none;
    font-size: 12px;
  }

  .info-row .value {
    font-size: 13px;
  }

  .stats-section {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    padding: 24px 0;
  }

  .stat-card {
    padding: 14px;
    border-radius: 8px;
  }

  .stat-label {
    font-size: 11px;
    margin-bottom: 6px;
  }

  .stat-value {
    font-size: 20px;
  }

  .list-section {
    padding: 10px 12px;
    max-width: 100%;
  }

  .list-header {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 12px;
    gap: 12px;
  }

  .list-header h2 {
    font-size: 16px;
    letter-spacing: -0.5px;
  }

  .ticket-count-info {
    font-size: 11px;
    margin-left: 6px;
  }

  .ticket-count-checkedin {
    font-size: 11px;
    margin-left: 6px;
  }

  .status-filter {
    gap: 6px;
    width: 100%;
  }

  .controls {
    gap: 8px;
    padding: 0px;
  }

  .search-sort-row {
    flex-direction: row;
    gap: 8px;
  }

  .search-input {
    height: 31px;
    font-size: 0.75rem;
  }

  .sort-button {
    width: 100%;
    padding: 8px 10px;
    font-size: 0.75rem;
    justify-content: center;
  }

  .sort-icon {
    width: 14px;
    height: 14px;
  }

  .filter-btn {
    flex: 1;
    padding: 6px 12px;
    font-size: 0.75rem;
  }

  .reservations-list {
    gap: 10px;
  }

  .summary-stats-card {
    padding: 10px 14px;
    margin-top: 2px;
    min-height: 48px;
    border-radius: 8px;
  }

  .summary-stats-content {
    gap: 6px;
  }

  .stats-label {
    font-size: 12px;
  }

  .stats-total {
    font-size: 17px;
  }

  .stats-breakdown {
    font-size: 12px;
  }

  .summary-stats-extra {
    min-width: 100%;
  }

  .stats-extra-row {
    gap: 10px;
    font-size: 12px;
  }

  .no-data {
    padding: 32px 16px;
    font-size: 13px;
    border-radius: 8px;
  }

  .reservation-card {
    border-radius: 8px;
  }

  .reservation-summary {
    padding: 12px 14px;
  }

  .summary-main {
    gap: 10px;
  }

  .summary-name {
    font-size: 13px;
    min-width: 40px;
    max-width: 80px;
  }

  .summary-email {
    display: none;
  }

  .summary-quantity {
    font-size: 12px;
    min-width: 32px;
  }

  .summary-amount {
    font-size: 12px;
    min-width: 50px;
  }

  .summary-status {
    gap: 6px;
  }

  .reservation-status {
    padding: 4px 8px;
    font-size: 10px;
    border-radius: 6px;
  }

  .not-checked-in-badge {
    padding: 4px 8px;
    font-size: 10px;
    border-radius: 6px;
  }

  .payment-badge {
    padding: 4px 10px;
    font-size: 10px;
    border-radius: 6px;
  }

  .expand-icon {
    font-size: 10px;
  }

  .reservation-detail {
    padding: 16px;
  }

  .detail-section {
    margin-bottom: 16px;
  }

  .detail-row {
    flex-direction: column;
    align-items: flex-start;
    padding: 10px 0;
    gap: 6px;
  }

  .custom-answers-section {
    margin-top: 16px;
  }

  .custom-answers-title {
    margin-bottom: 12px;
  }

  .custom-answers-title span {
    font-size: 12px;
    padding: 6px 10px;
  }

  .detail-label {
    font-size: 12px;
    min-width: auto;
  }

  .detail-value {
    font-size: 13px;
  }

  .detail-value-with-action {
    width: 100%;
    font-size: 13px;
    flex-direction: row;
    align-items: flex-start;
  }

  .copy-btn-small {
    font-size: 11px;
    padding: 4px 8px;
    border-radius: 6px;
  }

  .copy-btn-icon img {
    width: 15px;
    height: 15px;
  }

  .reservation-actions {
    flex-direction: column;
    gap: 10px;
    margin-top: 16px;
    padding-top: 16px;
  }

  .payment-btn,
  .btn-confirm,
  .btn-pending,
  .btn-refund-complete,
  .btn-cancel,
  .btn-check-in,
  .btn-uncheck-in {
    width: 100%;
    padding: 12px 16px;
    font-size: 14px;
    border-radius: 8px;
  }
}

/* ========================================
   초대권 등록
   ======================================== */
.invite-ticket-section {
  display: flex;
  justify-content: center;
  padding: 20px 0 8px;
}

.invite-ticket-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 12px 28px;
  background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-family: "NanumSquareRoundB", sans-serif;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(124, 58, 237, 0.3);
}

.invite-ticket-btn:hover {
  background: linear-gradient(135deg, #6d28d9 0%, #4c1d95 100%);
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.4);
  transform: translateY(-1px);
}

/* 초대권 모달 */
.invite-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.invite-modal {
  background: white;
  border-radius: 14px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  z-index: 1001;
  width: 90%;
  max-width: 500px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: visible;
}

.invite-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
}

.invite-modal-title {
  font-size: 18px;
  font-family: "NanumSquareRoundB", sans-serif;
  margin: 0;
  color: #1f2937;
}

.invite-modal-close {
  background: none;
  border: none;
  font-size: 28px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.invite-modal-close:hover {
  background: #f5f5f5;
  color: #666;
}

.invite-modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
  padding-bottom: 16px;
}

.invite-modal-desc {
  font-size: 13px;
  color: #666;
  background: #f5f0ff;
  border: 1px solid #ddd6fe;
  border-radius: 8px;
  padding: 12px 14px;
  margin: 0 0 20px;
  line-height: 1.6;
}

.invite-member-section {
  margin-bottom: 20px;
}

.invite-section-title {
  font-size: 14px;
  font-family: "NanumSquareRoundB", sans-serif;
  color: #374151;
  margin: 0 0 10px;
}

.invite-member-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.invite-member-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: #faf5ff;
  border: 1px solid #ede9fe;
  border-radius: 8px;
}

.invite-member-info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.invite-profile-image {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.invite-profile-placeholder {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
  flex-shrink: 0;
}

.invite-member-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.invite-member-nickname {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.invite-member-email {
  font-size: 12px;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.invite-ticket-badge {
  font-size: 11px;
  background: #7c3aed;
  color: white;
  padding: 3px 8px;
  border-radius: 20px;
  flex-shrink: 0;
  margin-left: 8px;
}

.invite-add-section {
  padding: 20px 24px;
  position: relative;
  overflow: visible;
  z-index: 10;
}

.invite-search-form {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  position: relative;
}

.invite-search-form :global(.invite-search-autocomplete) {
  flex: 1;
  width: 100%;
  position: relative;
}

.invite-search-form :global(.invite-search-autocomplete .p-autocomplete-input) {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;
  font-family: "NanumSquareRoundR", sans-serif;
  box-sizing: border-box;
}

.invite-search-form :global(.invite-search-autocomplete .p-autocomplete-input:focus) {
  outline: none;
  border-color: #7c3aed;
  box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.15);
}

.invite-search-form :global(.invite-search-autocomplete .p-autocomplete-input:disabled) {
  background: #f5f5f5;
  cursor: not-allowed;
}

.invite-add-btn {
  padding: 10px 18px;
  background: #7c3aed;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  font-family: "NanumSquareRoundB", sans-serif;
}

.invite-add-btn:hover:not(:disabled) {
  background: #6d28d9;
}

.invite-add-btn:disabled {
  background: #c4b5fd;
  cursor: not-allowed;
}

/* AutoComplete 드롭다운 */
:global(.invite-autocomplete-panel) {
  position: absolute !important;
  top: 100% !important;
  left: 0 !important;
  width: 100% !important;
  max-width: none !important;
  z-index: 1100 !important;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  background: white;
  margin-top: 4px !important;
}

:global(.invite-autocomplete-panel .p-autocomplete-items) {
  padding: 4px 0;
}

:global(.invite-autocomplete-panel .p-autocomplete-item) {
  padding: 0 !important;
  cursor: pointer;
  transition: background 0.2s;
}

:global(.invite-autocomplete-panel .p-autocomplete-item:hover) {
  background: #f5f5f5;
}

:global(.invite-autocomplete-panel .p-autocomplete-item.p-highlight) {
  background: #f5f0ff;
}

:global(.invite-user-option) {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
}

:global(.invite-option-profile-image) {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

:global(.invite-option-profile-placeholder) {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
  flex-shrink: 0;
}

:global(.invite-option-details) {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

:global(.invite-option-nickname) {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

:global(.invite-option-email) {
  font-size: 12px;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.invite-modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
}

.invite-close-btn {
  padding: 10px 24px;
  background: #f5f5f5;
  color: #666;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  font-family: "NanumSquareRoundR", sans-serif;
}

.invite-close-btn:hover:not(:disabled) {
  background: #e0e0e0;
}

.invite-close-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 480px) {
  .invite-modal {
    width: 95%;
    max-height: 90vh;
  }

  .invite-search-form {
    flex-direction: column;
  }

  .invite-add-btn {
    width: 100%;
  }
}
</style>
